import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { MEDIA } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useSettings } from "../contexts/SettingsContext";
import { ORDER_STATUS_RO, APPOINTMENT_STATUS_RO, TICKET_STATUS_RO } from "../lib/statusLabels";
import { User, ShoppingBag, Calendar, Ticket, Heart, Gift, ChevronRight, X, Copy, Check, AlertCircle, RotateCcw, Award, ArrowUp, ArrowDown } from "lucide-react";

export default function Account() {
    const { user, checkAuth } = useAuth();
    const { settings } = useSettings();
    const ticketsChatEnabled = !!settings?.tickets_client_chat_enabled;
    const [sp, setSp] = useSearchParams();
    const tab = sp.get("tab") || "profile";
    const [orders, setOrders] = React.useState([]);
    const [appointments, setAppointments] = React.useState([]);
    const [wishlistProducts, setWishlistProducts] = React.useState([]);
    const [credits, setCredits] = React.useState({ balance: 0, transactions: [] });
    const [referral, setReferral] = React.useState(null);
    const [loyalty, setLoyalty] = React.useState(null); // { points, tier, next_tier, redeem_value, transactions }
    const [tickets, setTickets] = React.useState([]);
    const [newTicket, setNewTicket] = React.useState({ subject: "", category: "general", description: "" });
    const [viewTicket, setViewTicket] = React.useState(null);
    const [ticketReply, setTicketReply] = React.useState("");
    const [profile, setProfile] = React.useState({ name: "", phone: "", county: "", city: "", postal_code: "", street: "", number: "", bloc_ap: "", password: "" });
    const [counties, setCounties] = React.useState([]);
    const [cities, setCities] = React.useState([]);
    const [viewOrder, setViewOrder] = React.useState(null);
    const [codeCopied, setCodeCopied] = React.useState(false);
    const [returns, setReturns] = React.useState([]);
    const [returnModal, setReturnModal] = React.useState(null); // order for which to file return
    const [returnForm, setReturnForm] = React.useState({ items: [], reason: "", description: "" });
    const [returnSubmitting, setReturnSubmitting] = React.useState(false);

    React.useEffect(() => {
        if (user) setProfile({ name: user.name || "", phone: user.phone || "", county: user.county || "", city: user.city || "", postal_code: user.postal_code || "", street: user.address || "", number: "", bloc_ap: "", password: "" });
    }, [user]);
    React.useEffect(() => { api.get("/locations/counties").then(r => setCounties(r.data || [])).catch(() => {}); }, []);
    React.useEffect(() => {
        if (profile.county) api.get("/locations/cities", { params: { county: profile.county } }).then(r => setCities(r.data || []));
        else setCities([]);
    }, [profile.county]);

    React.useEffect(() => {
        if (tab === "orders") api.get("/orders/my").then(r => setOrders(r.data || []));
        if (tab === "appointments") api.get("/appointments/my").then(r => setAppointments(r.data || []));
        if (tab === "credits") {
            api.get("/credits/balance").then(r => setCredits(r.data || { balance: 0, transactions: [] }));
            api.get("/referrals/my").then(r => setReferral(r.data));
        }
        if (tab === "wishlist") {
            api.get("/wishlist").then(async r => {
                if (r.data.product_ids?.length) {
                    const res = await api.get(`/products?limit=200`);
                    setWishlistProducts((res.data.items || []).filter(p => r.data.product_ids.includes(p.product_id)));
                } else setWishlistProducts([]);
            });
        }
        if (tab === "tickets") api.get("/tickets/my").then(r => setTickets(r.data || []));
        if (tab === "returns") api.get("/returns/my").then(r => setReturns(r.data || []));
        if (tab === "loyalty") {
            Promise.all([api.get("/loyalty/status"), api.get("/loyalty/history")])
                .then(([s, h]) => setLoyalty({ ...s.data, transactions: h.data || [] }))
                .catch(() => {});
        }
    }, [tab]);

    const submitTicket = async (e) => {
        e.preventDefault();
        if (!newTicket.subject.trim()) return toast.error("Completează subiectul");
        try {
            await api.post("/tickets", newTicket);
            toast.success("Ticket trimis! Te vom contacta în curând.");
            setNewTicket({ subject: "", category: "general", description: "" });
            const r = await api.get("/tickets/my"); setTickets(r.data || []);
        } catch { toast.error("Eroare la trimitere"); }
    };
    const openTicket = async (id) => { const res = await api.get(`/tickets/${id}`); setViewTicket(res.data); };
    const replyTicket = async () => {
        if (!ticketReply.trim()) return;
        await api.post(`/tickets/${viewTicket.ticket_id}/messages`, { body: ticketReply });
        setTicketReply("");
        const r = await api.get(`/tickets/${viewTicket.ticket_id}`); setViewTicket(r.data);
    };
    const saveProfile = async (e) => {
        e.preventDefault();
        try {
            const { street, number, bloc_ap, ...rest } = profile;
            const address = [street, number, bloc_ap].filter(Boolean).join(", ");
            const payload = { ...rest, address };
            if (!payload.password) delete payload.password;
            await api.put("/auth/profile", payload); await checkAuth(); toast.success("Profil actualizat");
        } catch { toast.error("Eroare"); }
    };
    const updateAppointment = async (id, status) => {
        try { await api.put(`/appointments/${id}/status`, { status }); toast.success("OK"); api.get("/appointments/my").then(r => setAppointments(r.data || [])); } catch { toast.error("Eroare"); }
    };
    const cancelOrder = async (id) => {
        if (!window.confirm("Anulezi comanda?")) return;
        try { await api.post(`/orders/${id}/cancel`); toast.success("Anulată"); api.get("/orders/my").then(r => setOrders(r.data || [])); } catch (e) { toast.error(e.response?.data?.detail || "Eroare"); }
    };
    const cancelAppt = async (id) => {
        if (!window.confirm("Anulezi programarea?")) return;
        try { await api.post(`/appointments/${id}/cancel`); toast.success("Anulată"); api.get("/appointments/my").then(r => setAppointments(r.data || [])); } catch { toast.error("Eroare"); }
    };
    const openOrder = async (id) => { const res = await api.get(`/orders/${id}`); setViewOrder(res.data); };
    const referralLink = referral?.code
        ? `${window.location.origin}/?ref=${referral.code}`
        : null;
    const copyCode = () => {
        navigator.clipboard.writeText(referralLink || referral?.code || "");
        setCodeCopied(true);
        toast.success("Copiat!");
        setTimeout(() => setCodeCopied(false), 2000);
    };

    const openReturnModal = (order) => {
        setReturnForm({
            items: order.items.map(it => ({ ...it, selected: true, return_qty: it.quantity })),
            reason: "",
            description: "",
        });
        setReturnModal(order);
    };

    const submitReturn = async (e) => {
        e.preventDefault();
        const selectedItems = returnForm.items
            .filter(it => it.selected && it.return_qty > 0)
            .map(({ selected, return_qty, ...rest }) => ({ ...rest, quantity: return_qty }));
        if (!selectedItems.length) return toast.error("Selectează cel puțin un produs");
        if (!returnForm.reason) return toast.error("Selectează motivul returului");
        setReturnSubmitting(true);
        try {
            await api.post("/returns", {
                order_id: returnModal.order_id,
                items: selectedItems,
                reason: returnForm.reason,
                description: returnForm.description,
            });
            toast.success("Cerere de retur trimisă!");
            setReturnModal(null);
            api.get("/returns/my").then(r => setReturns(r.data || []));
        } catch (err) {
            toast.error(err.response?.data?.detail || "Eroare la trimitere");
        } finally {
            setReturnSubmitting(false);
        }
    };

    const RETURN_STATUS_LABELS = {
        pending: "În așteptare", approved: "Aprobat", rejected: "Respins",
        processing: "În procesare", completed: "Finalizat",
    };
    const RETURNABLE_STATUSES = ["confirmed", "processing", "shipped", "delivered", "paid"];

    const isInstaller = user && ((user.roles || []).includes("installer") || user.role === "installer");
    const fmt = (n) => `${(n || 0).toFixed(2)} RON`;

    const TABS = [
        { key: "profile", label: "Profil", icon: User },
        { key: "orders", label: "Comenzi", icon: ShoppingBag },
        { key: "returns", label: "Retururi", icon: RotateCcw },
        { key: "appointments", label: isInstaller ? "Instalări" : "Programări", icon: Calendar },
        { key: "tickets", label: "Support", icon: Ticket },
        { key: "wishlist", label: "Favorite", icon: Heart },
        { key: "credits", label: "Credite", icon: Gift },
        { key: "loyalty", label: "Loialitate", icon: Award },
    ];

    return (
        <div className="container-x py-8 sm:py-12 animate-fade-up" data-testid="account-page">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                    <User className="h-7 w-7" />
                </div>
                <div>
                    <h1 className="font-heading text-2xl sm:text-3xl">Bună, {user?.name}!</h1>
                    <div className="text-sm text-muted-foreground mt-0.5">
                        {user?.email}
                        {user?.custom_discount_percent > 0 && (
                            <span className="ml-2 inline-flex items-center gap-1 bg-primary/10 text-primary text-xs font-semibold px-2 py-0.5 rounded-full">
                                -{user.custom_discount_percent}% discount cont
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Staff CTA */}
            {["superadmin", "admin", "sales", "installer"].includes(user?.role) && (
                <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5 mb-8 flex items-center justify-between gap-4 flex-wrap" data-testid="staff-panel-cta">
                    <div>
                        <div className="font-heading text-lg mb-1">
                            {user.role === "installer" ? "Tickete & instalări atribuite" : user.role === "sales" ? "Panou operațional" : "Panou de administrare"}
                        </div>
                        <div className="text-sm text-muted-foreground">
                            {user.role === "installer" ? "Tickete asignate, agendă, programări cu certificat de garanție." : user.role === "sales" ? "Comenzi, facturi, inventar, clienți, agendă — toate la un loc." : "Acces complet: produse, comenzi, facturi, setări, temă."}
                        </div>
                    </div>
                    <Link to={user.role === "installer" ? "/admin/tickets" : "/admin"} className="btn-primary whitespace-nowrap" data-testid="staff-panel-go">
                        {user.role === "installer" ? "Mergi la Tickete →" : "Deschide panoul →"}
                    </Link>
                </div>
            )}

            {/* Tabs */}
            <div className="overflow-x-auto no-scrollbar mb-8">
                <div className="flex gap-1 border-b border-border min-w-max">
                    {TABS.map(({ key, label, icon: Icon }) => (
                        <button key={key} onClick={() => setSp({ tab: key })}
                            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium whitespace-nowrap transition-colors ${tab === key ? "border-b-2 border-primary text-primary" : "text-muted-foreground hover:text-foreground"}`}
                            data-testid={`account-tab-${key}`}>
                            <Icon className="h-4 w-4" />
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Profile */}
            {tab === "profile" && (
                <form onSubmit={saveProfile} className="max-w-lg space-y-4">
                    {/* Post-Google-login prompt: missing address or phone */}
                    {(!user?.phone || !user?.address) && (
                        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3" data-testid="profile-complete-banner">
                            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <div className="font-medium text-amber-900 text-sm">Completează-ți profilul</div>
                                <p className="text-xs text-amber-700 mt-0.5">
                                    Adaugă numărul de telefon și adresa de livrare pentru a plasa comenzi mai rapid.
                                </p>
                            </div>
                        </div>
                    )}
                    <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
                        <h3 className="font-heading text-xl">Date personale</h3>
                        <input required placeholder="Nume complet" value={profile.name} onChange={(e) => setProfile(p => ({ ...p, name: e.target.value }))} className="input-soft" data-testid="profile-name" />
                        <input placeholder="Telefon" value={profile.phone} onChange={(e) => setProfile(p => ({ ...p, phone: e.target.value }))} className="input-soft" data-testid="profile-phone" />
                    </div>
                    <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
                        <h3 className="font-heading text-xl">Adresă de livrare</h3>
                        <div className="grid grid-cols-2 gap-3">
                            <select value={profile.county} onChange={(e) => setProfile(p => ({ ...p, county: e.target.value, city: "" }))} className="input-soft">
                                <option value="">Județ</option>
                                {counties.map(c => <option key={c}>{c}</option>)}
                            </select>
                            <div>
                                <input list="profile-cities-list" placeholder="Localitate"
                                       value={profile.city} onChange={(e) => setProfile(p => ({ ...p, city: e.target.value }))} className="input-soft" />
                                <datalist id="profile-cities-list">{cities.map(c => <option key={c} value={c} />)}</datalist>
                            </div>
                        </div>
                        <input placeholder="Cod poștal" value={profile.postal_code} onChange={(e) => setProfile(p => ({ ...p, postal_code: e.target.value }))} className="input-soft" />
                        <div className="grid grid-cols-3 gap-3">
                            <input placeholder="Strada" value={profile.street} onChange={(e) => setProfile(p => ({ ...p, street: e.target.value }))} className="input-soft col-span-2" data-testid="profile-address" />
                            <input placeholder="Nr." value={profile.number} onChange={(e) => setProfile(p => ({ ...p, number: e.target.value }))} className="input-soft" />
                        </div>
                        <input placeholder="Bloc / Scară / Apartament (opțional)" value={profile.bloc_ap} onChange={(e) => setProfile(p => ({ ...p, bloc_ap: e.target.value }))} className="input-soft" />
                    </div>
                    <div className="bg-card border border-border rounded-2xl p-6 space-y-3">
                        <h3 className="font-heading text-xl">Securitate</h3>
                        <label className="text-xs text-muted-foreground block -mb-1">Schimbă parola (lasă gol pentru a păstra parola actuală)</label>
                        <input type="password" placeholder="Parolă nouă" value={profile.password} onChange={(e) => setProfile(p => ({ ...p, password: e.target.value }))} className="input-soft" />
                    </div>
                    <button type="submit" className="btn-primary" data-testid="profile-save">Salvează modificările</button>
                </form>
            )}

            {/* Orders */}
            {tab === "orders" && (
                <div className="space-y-3">
                    {orders.length === 0 && (
                        <div className="py-12 text-center text-muted-foreground">
                            <ShoppingBag className="h-10 w-10 mx-auto mb-3 opacity-30" />
                            <p>Nu ai comenzi încă.</p>
                            <Link to="/magazin" className="btn-primary mt-4 inline-flex">Mergi la magazin</Link>
                        </div>
                    )}
                    {orders.map(o => (
                        <button type="button" key={o.order_id} onClick={() => openOrder(o.order_id)}
                            className="bg-card border border-border/60 rounded-2xl p-4 sm:p-5 w-full text-left hover:border-primary/40 hover:shadow-md transition-all duration-200"
                            data-testid={`order-${o.order_id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <div className="text-xs text-muted-foreground font-mono mb-1">#{o.order_number || o.order_id.slice(-10)}</div>
                                    <div className="font-semibold">{new Date(o.created_at).toLocaleString("ro-RO")}</div>
                                    <div className="text-sm text-muted-foreground mt-0.5">{o.items?.length} produs(e)</div>
                                    {o.awb && <div className="text-xs mt-1.5 font-mono text-muted-foreground">AWB: {o.awb}</div>}
                                </div>
                                <div className="text-right flex-shrink-0">
                                    <div className={`chip text-xs mb-2 ${o.payment_status === "paid" ? "chip-success" : "bg-muted text-muted-foreground"}`}>{ORDER_STATUS_RO[o.status] || o.status}</div>
                                    <div className="font-heading text-xl font-bold text-primary">{fmt(o.total)}</div>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            )}

            {/* Appointments */}
            {tab === "appointments" && (
                <div className="space-y-3">
                    {appointments.length === 0 && (
                        <div className="py-12 text-center text-muted-foreground">
                            <Calendar className="h-10 w-10 mx-auto mb-3 opacity-30" />
                            <p>Nu ai {isInstaller ? "instalări atribuite" : "programări"} încă.</p>
                        </div>
                    )}
                    {appointments.map(a => (
                        <div key={a.appointment_id} className="bg-card border border-border/60 rounded-2xl p-4 sm:p-5" data-testid={`appointment-${a.appointment_id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                    <div className="text-xs text-muted-foreground font-mono mb-1">{a.date || "—"} · {a.time_slot || "în așteptare"}</div>
                                    <div className="font-semibold">{a.service_type}</div>
                                    <div className="text-sm text-muted-foreground mt-0.5">{a.address}, {a.city}</div>
                                    {isInstaller && <div className="text-sm mt-2 font-medium">{a.name} · {a.phone}</div>}
                                </div>
                                <div className={`chip text-xs flex-shrink-0 ${a.status === "confirmed" ? "chip-primary" : a.status === "done" ? "chip-success" : "bg-muted text-muted-foreground"}`}>
                                    {APPOINTMENT_STATUS_RO[a.status] || a.status}
                                </div>
                            </div>
                            {isInstaller && a.status !== "done" && a.status !== "canceled" && (
                                <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-border">
                                    {a.status === "pending" && <button onClick={() => updateAppointment(a.appointment_id, "confirmed")} className="btn-outline !py-1.5 text-xs">Confirmă</button>}
                                    <button onClick={() => updateAppointment(a.appointment_id, "in_progress")} className="btn-outline !py-1.5 text-xs">În desfășurare</button>
                                    <button onClick={() => updateAppointment(a.appointment_id, "done")} className="btn-primary !py-1.5 text-xs">Finalizată</button>
                                </div>
                            )}
                            {!isInstaller && !["in_progress", "done", "canceled"].includes(a.status) && (
                                <button onClick={() => cancelAppt(a.appointment_id)} className="btn-ghost text-destructive text-xs mt-2">Anulează programarea</button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Credits */}
            {tab === "credits" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-2xl">
                    <div className="bg-card border border-border rounded-2xl p-6">
                        <div className="overline mb-2">Credite disponibile</div>
                        <div className="font-heading text-4xl font-bold text-primary mb-1">{fmt(credits.balance)}</div>
                        <p className="text-sm text-muted-foreground mb-4">Folosibile la viitoarele comenzi.</p>
                        {credits.transactions?.length > 0 && (
                            <div className="space-y-1 max-h-48 overflow-y-auto text-sm border-t border-border pt-3">
                                {credits.transactions.map(t => (
                                    <div key={t.tx_id} className="flex justify-between py-1.5">
                                        <span className="text-muted-foreground text-xs">{t.reason}</span>
                                        <span className={`text-xs font-semibold ${t.amount > 0 ? "text-primary" : "text-destructive"}`}>{t.amount > 0 ? "+" : ""}{fmt(t.amount)}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="bg-card border border-border rounded-2xl p-6">
                        <div className="overline mb-2">Program de recomandare</div>
                        <p className="text-sm text-muted-foreground mb-4">Primești 5% din prima comandă a prietenilor invitați ca și credit.</p>
                        <div className="bg-muted rounded-xl p-3 flex items-center justify-between gap-2 mb-3">
                            <span className="font-mono text-sm font-bold break-all select-all">{referralLink || "..."}</span>
                            <button onClick={copyCode} className="btn-ghost !px-3 !py-1.5 text-xs flex items-center gap-1.5 flex-shrink-0">
                                {codeCopied ? <Check className="h-3.5 w-3.5 text-primary" /> : <Copy className="h-3.5 w-3.5" />}
                                {codeCopied ? "Copiat!" : "Copiază"}
                            </button>
                        </div>
                        {referral && <div className="text-xs text-muted-foreground">{referral.referred_count} prieteni înscriși</div>}
                    </div>
                </div>
            )}

            {/* Loyalty */}
            {tab === "loyalty" && (
                <div className="space-y-5 max-w-2xl">
                    {!loyalty ? (
                        <div className="py-12 text-center text-muted-foreground">Se încarcă...</div>
                    ) : (
                        <>
                            {/* Tier card */}
                            {(() => {
                                const tierName = (loyalty.tier?.name || "Bronze").toLowerCase();
                                const tierDisc = loyalty.tier?.discount_percent || 0;
                                const bgCls = tierName === "gold" ? "bg-amber-50 border-amber-300" : tierName === "silver" ? "bg-slate-50 border-slate-300" : "bg-orange-50 border-orange-200";
                                const iconCls = tierName === "gold" ? "text-amber-500" : tierName === "silver" ? "text-slate-500" : "text-orange-400";
                                const textCls = tierName === "gold" ? "text-amber-600" : tierName === "silver" ? "text-slate-600" : "text-orange-500";
                                return (
                                    <div className={`rounded-2xl p-6 border ${bgCls}`}>
                                        <div className="flex items-center justify-between gap-4 flex-wrap">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <Award className={`h-5 w-5 ${iconCls}`} />
                                                    <span className={`font-heading text-xl font-bold ${textCls}`}>{loyalty.tier?.name || "Bronze"}</span>
                                                </div>
                                                <div className="text-3xl font-heading font-bold">{loyalty.points} <span className="text-base font-normal text-muted-foreground">puncte</span></div>
                                                {loyalty.redeem_value > 0 && (
                                                    <div className="text-sm text-muted-foreground mt-1">= {loyalty.redeem_value.toFixed(2)} RON reducere disponibilă</div>
                                                )}
                                            </div>
                                            {tierDisc > 0 && (
                                                <div className="text-center">
                                                    <div className={`text-2xl font-heading font-bold ${textCls}`}>-{tierDisc}%</div>
                                                    <div className="text-xs text-muted-foreground">discount tier</div>
                                                </div>
                                            )}
                                        </div>
                                        {loyalty.next_tier && (
                                            <div className="mt-4">
                                                <div className="flex justify-between text-xs text-muted-foreground mb-1">
                                                    <span>{loyalty.points} pct.</span>
                                                    <span>{loyalty.next_tier.min_points} pct. → {loyalty.next_tier.name}</span>
                                                </div>
                                                <div className="h-2 bg-white/60 rounded-full overflow-hidden">
                                                    <div className="h-full rounded-full bg-primary transition-all"
                                                        style={{ width: `${Math.min(100, (loyalty.points / loyalty.next_tier.min_points) * 100)}%` }} />
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-1">{Math.max(0, loyalty.next_tier.min_points - loyalty.points)} puncte până la {loyalty.next_tier.name}</div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* How it works */}
                            <div className="bg-card border border-border rounded-2xl p-5">
                                <h4 className="font-heading text-base mb-3">Cum funcționează</h4>
                                <div className="space-y-2 text-sm text-muted-foreground">
                                    <div className="flex items-start gap-2"><span className="text-primary font-bold">1.</span> Câștigă 1 punct la fiecare <strong>{loyalty.ron_per_point || 10} RON</strong> cheltuiți.</div>
                                    <div className="flex items-start gap-2"><span className="text-primary font-bold">2.</span> <strong>{loyalty.min_redeem || 100} puncte</strong> = <strong>{((loyalty.min_redeem || 100) * (loyalty.redemption_rate || 0.1)).toFixed(2)} RON</strong> reducere la checkout.</div>
                                    <div className="flex items-start gap-2"><span className="text-primary font-bold">3.</span>
                                        <div>Niveluri: {(loyalty.tiers || []).slice().sort((a,b) => a.min_points - b.min_points).map((t, i) => (
                                            <span key={i}>{i > 0 && ", "}<strong style={{color: t.color}}>{t.name}</strong> ({t.min_points} pct.{t.discount_percent > 0 ? `, ${t.discount_percent}% extra` : ""})</span>
                                        ))}.</div>
                                    </div>
                                </div>
                            </div>

                            {/* History */}
                            {loyalty.transactions?.length > 0 && (
                                <div className="bg-card border border-border rounded-2xl p-5">
                                    <h4 className="font-heading text-base mb-3">Istoric puncte</h4>
                                    <div className="space-y-1 max-h-56 overflow-y-auto">
                                        {loyalty.transactions.map((t, i) => (
                                            <div key={i} className="flex items-center justify-between py-1.5 border-b border-border/40 last:border-0">
                                                <div>
                                                    <div className="text-sm">{t.reason || t.type}</div>
                                                    <div className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString("ro-RO")}</div>
                                                </div>
                                                <span className={`flex items-center gap-0.5 text-sm font-semibold ${t.points > 0 ? "text-emerald-600" : "text-destructive"}`}>
                                                    {t.points > 0 ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />}
                                                    {Math.abs(t.points)} pct.
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Tickets */}
            {tab === "tickets" && (
                <div className="space-y-6">
                    <div className="bg-card border border-border rounded-2xl p-6 max-w-2xl">
                        <h3 className="font-heading text-xl mb-1">Deschide un ticket</h3>
                        <p className="text-sm text-muted-foreground mb-4">Problemă cu un produs, instalarea sau garanția? Te ajutăm imediat.</p>
                        <form onSubmit={submitTicket} className="space-y-3">
                            <input required placeholder="Subiect" value={newTicket.subject} onChange={(e) => setNewTicket(t => ({ ...t, subject: e.target.value }))} className="input-soft" data-testid="ticket-subject" />
                            <select value={newTicket.category} onChange={(e) => setNewTicket(t => ({ ...t, category: e.target.value }))} className="input-soft" data-testid="ticket-category">
                                <option value="general">Generală</option>
                                <option value="warranty">Garanție</option>
                                <option value="installation">Instalare</option>
                                <option value="malfunction">Defecțiune</option>
                                <option value="return">Retur</option>
                            </select>
                            <textarea required rows="4" placeholder="Descrie problema..." value={newTicket.description} onChange={(e) => setNewTicket(t => ({ ...t, description: e.target.value }))} className="input-soft" data-testid="ticket-description" />
                            <button type="submit" className="btn-primary" data-testid="ticket-submit">Trimite ticket</button>
                        </form>
                    </div>
                    <div className="space-y-3 max-w-2xl">
                        {tickets.length === 0 && <p className="text-muted-foreground text-sm">Nu ai tickete deschise.</p>}
                        {tickets.map(t => (
                            <button key={t.ticket_id} type="button" onClick={() => openTicket(t.ticket_id)}
                                className="bg-card border border-border/60 rounded-2xl p-4 w-full text-left hover:border-primary/40 hover:shadow-md transition-all duration-200"
                                data-testid={`ticket-${t.ticket_id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs text-muted-foreground font-mono mb-1">#{t.ticket_number} · {new Date(t.created_at).toLocaleDateString("ro-RO")}</div>
                                        <div className="font-semibold truncate">{t.subject}</div>
                                        <div className="text-xs text-muted-foreground mt-1 line-clamp-1">{(t.description || "").slice(0, 100)}</div>
                                    </div>
                                    <div className={`chip text-xs flex-shrink-0 ${t.status === "resolved" || t.status === "closed" ? "chip-success" : "chip-primary"}`}>
                                        {TICKET_STATUS_RO[t.status] || t.status}
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Wishlist */}
            {tab === "wishlist" && (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                    {wishlistProducts.length === 0 && (
                        <div className="col-span-full py-12 text-center text-muted-foreground">
                            <Heart className="h-10 w-10 mx-auto mb-3 opacity-30" />
                            <p>Nu ai produse favorite încă.</p>
                            <Link to="/magazin" className="btn-primary mt-4 inline-flex">Descoperă produse</Link>
                        </div>
                    )}
                    {wishlistProducts.map(p => (
                        <Link key={p.product_id} to={`/produs/${p.slug}`}
                            className="bg-card border border-border/60 rounded-2xl overflow-hidden hover:border-primary/40 hover:shadow-md transition-all duration-200 group">
                            <div className="aspect-[4/3] bg-muted overflow-hidden">
                                {p.images?.[0] && <img src={MEDIA(p.images[0])} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                            </div>
                            <div className="p-3">
                                <div className="font-semibold text-sm line-clamp-2">{p.name}</div>
                                <div className="text-primary font-heading font-bold mt-1">{(p.sale_price || p.price).toFixed(2)} RON</div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}

            {/* Ticket detail modal */}
            {viewTicket && (
                <div className="fixed inset-0 z-[200] bg-foreground/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={() => setViewTicket(null)}>
                    <div className="bg-card rounded-t-2xl sm:rounded-2xl max-w-2xl w-full max-h-[88vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="p-5 border-b border-border flex items-center justify-between">
                            <div>
                                <h3 className="font-heading text-xl">{viewTicket.subject}</h3>
                                <div className="text-xs text-muted-foreground font-mono">#{viewTicket.ticket_number} · {TICKET_STATUS_RO[viewTicket.status] || viewTicket.status}</div>
                            </div>
                            <button onClick={() => setViewTicket(null)} className="btn-ghost"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="p-5 space-y-3">
                            <div className="bg-muted p-3 rounded-xl text-sm">
                                <div className="text-xs text-muted-foreground mb-1">Descriere inițială</div>
                                {viewTicket.description}
                            </div>
                            {viewTicket.resolution && (
                                <div className="bg-primary/5 border border-primary/30 rounded-xl p-3 text-sm">
                                    <div className="text-xs text-primary font-semibold mb-1">Rezoluție</div>
                                    <div className="whitespace-pre-line">{viewTicket.resolution}</div>
                                </div>
                            )}
                            {(viewTicket.messages || []).map((m, i) => (
                                <div key={i} className={`rounded-xl p-3 text-sm ${m.is_staff ? "bg-primary/10 border border-primary/20" : "bg-muted"}`}>
                                    <div className="text-xs text-muted-foreground mb-1">{m.is_staff ? "Echipa · " : ""}{m.author_name} · {new Date(m.created_at).toLocaleString("ro-RO")}</div>
                                    <div className="whitespace-pre-line">{m.body}</div>
                                </div>
                            ))}
                            {viewTicket.status !== "closed" && ticketsChatEnabled && (
                                <div className="pt-2 border-t border-border">
                                    <textarea value={ticketReply} onChange={(e) => setTicketReply(e.target.value)} rows="3" placeholder="Răspunde..." className="input-soft" data-testid="ticket-reply" />
                                    <button onClick={replyTicket} className="btn-primary mt-2" data-testid="ticket-reply-send">Trimite răspuns</button>
                                </div>
                            )}
                            {!ticketsChatEnabled && viewTicket.status !== "closed" && (
                                <div className="flex items-start gap-2 p-3 bg-muted rounded-xl text-xs text-muted-foreground">
                                    <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                                    Pentru detalii suplimentare, contactează-ne telefonic sau prin email.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Returns tab */}
            {tab === "returns" && (
                <div className="space-y-4 max-w-2xl">
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">Cererile tale de retur. Procesăm retururile în 3-5 zile lucrătoare.</p>
                    </div>
                    {returns.length === 0 && (
                        <div className="py-12 text-center text-muted-foreground">
                            <RotateCcw className="h-10 w-10 mx-auto mb-3 opacity-30" />
                            <p>Nu ai cereri de retur.</p>
                            <p className="text-xs mt-1">Poți solicita un retur din secțiunea Comenzi.</p>
                        </div>
                    )}
                    {returns.map(r => (
                        <div key={r.return_id} className="bg-card border border-border/60 rounded-2xl p-4 sm:p-5">
                            <div className="flex items-start justify-between gap-3 mb-3">
                                <div>
                                    <div className="text-xs text-muted-foreground font-mono mb-1">Comanda #{r.order_number}</div>
                                    <div className="font-semibold text-sm">{r.reason}</div>
                                    {r.description && <div className="text-xs text-muted-foreground mt-0.5">{r.description}</div>}
                                </div>
                                <span className={`chip text-xs flex-shrink-0 ${r.status === "completed" ? "chip-success" : r.status === "rejected" ? "bg-destructive/10 text-destructive" : r.status === "approved" ? "chip-primary" : "bg-muted text-muted-foreground"}`}>
                                    {RETURN_STATUS_LABELS[r.status] || r.status}
                                </span>
                            </div>
                            <div className="space-y-1">
                                {r.items.map((it, i) => (
                                    <div key={i} className="text-xs text-muted-foreground flex justify-between">
                                        <span>{it.name}</span>
                                        <span>× {it.quantity}</span>
                                    </div>
                                ))}
                            </div>
                            {r.admin_notes && (
                                <div className="mt-3 pt-3 border-t border-border text-xs bg-muted rounded-lg p-2 text-muted-foreground">
                                    <strong>Notă:</strong> {r.admin_notes}
                                </div>
                            )}
                            <div className="text-xs text-muted-foreground mt-2">{new Date(r.created_at).toLocaleDateString("ro-RO")}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* Order detail modal */}
            {viewOrder && (
                <div className="fixed inset-0 z-[200] bg-foreground/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={() => setViewOrder(null)}>
                    <div className="bg-card rounded-t-2xl sm:rounded-2xl max-w-2xl w-full max-h-[88vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
                        <div className="p-5 border-b border-border flex items-center justify-between">
                            <div>
                                <h3 className="font-heading text-xl">Comanda #{viewOrder.order_number || viewOrder.order_id.slice(-10)}</h3>
                                <div className="text-xs text-muted-foreground font-mono">{new Date(viewOrder.created_at).toLocaleString("ro-RO")}</div>
                            </div>
                            <button onClick={() => setViewOrder(null)} className="btn-ghost"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div className="bg-muted rounded-2xl divide-y divide-border overflow-hidden">
                                {(viewOrder.items || []).map((it, i) => (
                                    <div key={i} className="p-3 flex gap-3 items-center">
                                        <div className="w-14 h-14 bg-card rounded-xl overflow-hidden flex-shrink-0">
                                            {it.image && <img src={MEDIA(it.image)} alt="" className="w-full h-full object-cover" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium text-sm truncate">{it.name}</div>
                                            <div className="text-xs text-muted-foreground">{fmt(it.unit_price)} × {it.quantity}</div>
                                        </div>
                                        <div className="font-semibold text-sm flex-shrink-0">{fmt(it.unit_price * it.quantity)}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="bg-card border border-border rounded-xl p-4 text-sm space-y-2">
                                <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>{fmt(viewOrder.subtotal)}</span></div>
                                {viewOrder.discount_total > 0 && <div className="flex justify-between text-primary font-medium"><span>Discount</span><span>-{fmt(viewOrder.discount_total)}</span></div>}
                                <div className="flex justify-between text-muted-foreground"><span>Livrare</span><span>{fmt(viewOrder.shipping_cost)}</span></div>
                                <div className="flex justify-between font-heading text-lg pt-2 border-t border-border"><span>Total</span><span className="text-primary">{fmt(viewOrder.total)}</span></div>
                            </div>
                            {viewOrder.awb && (
                                <div className="bg-muted rounded-xl p-4">
                                    <div className="text-xs text-muted-foreground mb-1">Urmărire colet</div>
                                    <div className="font-mono font-semibold">{viewOrder.awb}</div>
                                    {viewOrder.tracking_url && <a href={viewOrder.tracking_url} target="_blank" rel="noopener noreferrer" className="text-primary text-sm flex items-center gap-1 mt-1">Urmărește coletul <ChevronRight className="h-3 w-3" /></a>}
                                </div>
                            )}
                            {viewOrder.invoice_file && (
                                <a href={`${process.env.REACT_APP_BACKEND_URL}${viewOrder.invoice_file}`} target="_blank" rel="noopener noreferrer" className="btn-outline w-full justify-center">Descarcă factura</a>
                            )}
                            {!["processing", "shipped", "delivered", "canceled"].includes(viewOrder.status) && (
                                <button onClick={() => { cancelOrder(viewOrder.order_id); setViewOrder(null); }} className="btn-outline w-full justify-center text-destructive border-destructive hover:bg-destructive/10">Anulează comanda</button>
                            )}
                            {RETURNABLE_STATUSES.includes(viewOrder.status) && !viewOrder.has_return_request && (
                                <button onClick={() => { setViewOrder(null); openReturnModal(viewOrder); }} className="btn-outline w-full justify-center flex items-center gap-2">
                                    <RotateCcw className="h-4 w-4" /> Solicită retur
                                </button>
                            )}
                            {viewOrder.has_return_request && (
                                <div className="text-center text-xs text-muted-foreground py-2">Cerere de retur înregistrată</div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {/* Return request modal */}
            {returnModal && (
                <div className="fixed inset-0 z-[200] bg-foreground/60 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4" onClick={() => setReturnModal(null)}>
                    <form onSubmit={submitReturn} className="bg-card rounded-t-2xl sm:rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
                        <div className="p-5 border-b border-border flex items-center justify-between">
                            <div>
                                <h3 className="font-heading text-xl">Cerere retur</h3>
                                <div className="text-xs text-muted-foreground">Comanda #{returnModal.order_number || returnModal.order_id.slice(-8)}</div>
                            </div>
                            <button type="button" onClick={() => setReturnModal(null)} className="btn-ghost"><X className="h-5 w-5" /></button>
                        </div>
                        <div className="p-5 space-y-4">
                            <div>
                                <div className="text-sm font-medium mb-2">Produse de returnat</div>
                                <div className="space-y-2">
                                    {returnForm.items.map((it, i) => (
                                        <label key={i} className={`flex items-center gap-3 p-3 rounded-xl border transition-colors cursor-pointer ${it.selected ? "border-primary bg-primary/5" : "border-border"}`}>
                                            <input type="checkbox" checked={it.selected}
                                                onChange={e => setReturnForm(f => ({ ...f, items: f.items.map((x, j) => j === i ? { ...x, selected: e.target.checked } : x) }))}
                                                className="accent-primary flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium truncate">{it.name}</div>
                                                <div className="text-xs text-muted-foreground">{fmt(it.unit_price)} × comandat: {it.quantity}</div>
                                            </div>
                                            {it.selected && (
                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                    <button type="button" onClick={() => setReturnForm(f => ({ ...f, items: f.items.map((x, j) => j === i ? { ...x, return_qty: Math.max(1, x.return_qty - 1) } : x) }))} className="btn-ghost !p-1 text-xs">−</button>
                                                    <span className="w-6 text-center text-sm font-semibold">{it.return_qty}</span>
                                                    <button type="button" onClick={() => setReturnForm(f => ({ ...f, items: f.items.map((x, j) => j === i ? { ...x, return_qty: Math.min(it.quantity, x.return_qty + 1) } : x) }))} className="btn-ghost !p-1 text-xs">+</button>
                                                </div>
                                            )}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-sm font-medium block mb-1">Motiv retur *</label>
                                <select required value={returnForm.reason} onChange={e => setReturnForm(f => ({ ...f, reason: e.target.value }))} className="input-soft">
                                    <option value="">Selectează motivul...</option>
                                    <option>Produs defect / deteriorat</option>
                                    <option>Nu corespunde descrierii</option>
                                    <option>Comandat din greșeală / S-a schimbat decizia</option>
                                    <option>Produs incorect primit</option>
                                    <option>Alt motiv</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-sm font-medium block mb-1">Detalii suplimentare</label>
                                <textarea rows={3} placeholder="Descrie problema sau motivul returului..." value={returnForm.description} onChange={e => setReturnForm(f => ({ ...f, description: e.target.value }))} className="input-soft" />
                            </div>
                            <button type="submit" disabled={returnSubmitting} className="btn-primary w-full">
                                {returnSubmitting ? "Se trimite..." : "Trimite cererea de retur"}
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
